import { useMutation } from '@apollo/client';
import type {
  UpdateAssessmentInput,
  UpdateInternalAuditReportMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  UpdateInternalAuditReportDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateInternalAuditReportTRPC } from './useUpdateInternalAuditReportTRPC';

/**
 * The wrapper's public input: the legacy GraphQL input plus the
 * optimistic-concurrency token (COI-622), which rides only on the tRPC leg.
 */
type WrapperInput = UpdateAssessmentInput & {
  OriginalTimestamp?: string | null;
};

export const useUpdateInternalAuditReport = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateInternalAuditReportGraphQL, graphqlState] = useMutation(
    UpdateInternalAuditReportDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_assessment_result');
        evictField(cache, 'document_assessment_result');
        evictField(cache, 'obligation_assessment_result');
        evictField(cache, 'internal_audit_entity');
        evictField(cache, 'internal_audit_report');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateInternalAuditReportTRPC();

  const updateInternalAuditReport = async (
    variables: WrapperInput
  ): Promise<UpdateInternalAuditReportMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateInternalAuditReport({
        ...variables,
        Title: variables.Title ?? '',
        Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
      });
    }

    const { OriginalTimestamp: _token, ...graphqlVariables } = variables;
    const result = await updateInternalAuditReportGraphQL({
      variables: { object: graphqlVariables },
    });
    if (!result.data) {
      throw new Error('Failed to update internal audit report');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateInternalAuditReport,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateInternalAuditReport,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
